import { useEffect, useState } from "react";

/**
 * Retorna o tempo decorrido formatado entre `startedAt` e `endedAt` (ou agora,
 * se `endedAt` for nulo). Atualiza automaticamente a cada `tickMs` enquanto
 * estiver "ao vivo" (sem `endedAt`).
 *
 * Formato:
 *   <1s   → "0s"
 *   <60s  → "Ns"
 *   <60m  → "Nm Ks"
 *   ≥60m  → "Nh Mm"
 */
export function useElapsedTime(
	startedAt: string | Date | null,
	endedAt?: string | Date | null,
	tickMs: number = 1000,
): string {
	const isLive = !endedAt && !!startedAt;
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!isLive) return;
		const id = setInterval(() => setNow(Date.now()), tickMs);
		return () => clearInterval(id);
	}, [isLive, tickMs]);

	if (!startedAt) return "—";
	const startMs = typeof startedAt === "string" ? Date.parse(startedAt) : startedAt.getTime();
	const endMs = endedAt
		? typeof endedAt === "string"
			? Date.parse(endedAt)
			: endedAt.getTime()
		: now;

	const ms = Math.max(0, endMs - startMs);
	if (!Number.isFinite(ms)) return "—";

	if (ms < 1000) return "0s";
	if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
	if (ms < 3_600_000) {
		const m = Math.floor(ms / 60_000);
		const s = Math.floor((ms % 60_000) / 1000);
		return `${m}m ${s}s`;
	}
	const h = Math.floor(ms / 3_600_000);
	const m = Math.floor((ms % 3_600_000) / 60_000);
	return `${h}h ${m}m`;
}
